import {ModuleWithProviders, NgModule} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {RouterModule} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';

import {AccordionModule} from 'ngx-bootstrap/accordion';
import {AlertModule} from 'ngx-bootstrap/alert';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {CarouselModule} from 'ngx-bootstrap/carousel';
import {CollapseModule} from 'ngx-bootstrap/collapse';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {ModalModule} from 'ngx-bootstrap/modal';
import {PaginationModule} from 'ngx-bootstrap/pagination';
import {ProgressbarModule} from 'ngx-bootstrap/progressbar';
import {RatingModule} from 'ngx-bootstrap/rating';
import {TabsModule} from 'ngx-bootstrap/tabs';
import {TimepickerModule} from 'ngx-bootstrap/timepicker';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {PopoverModule} from 'ngx-bootstrap/popover';
import {TypeaheadModule} from 'ngx-bootstrap/typeahead';
import {BsDatepickerModule, DatepickerModule} from 'ngx-bootstrap/datepicker';

import {SparklineDirective} from './directives/sparkline/sparkline.directive';
import {ColorsService} from './colors/colors.service';
import {CheckallDirective} from './directives/checkall/checkall.directive';
import {NowDirective} from './directives/now/now.directive';
import {JqcloudDirective} from './directives/jqcloud/jqcloud.directive';
import {UserService} from './services/user.service';
import {AuthenticationService} from './services/authentication.service';
import {AuthGuard} from './guards/auth.guard';
import {HTTP_INTERCEPTORS} from '@angular/common/http';
import {AdminHttpInterceptor} from './utils/admin.http.interceptor';
import {HttpErrorInterceptor} from './utils/error.interceptor';
import {EchartsUtils} from './utils/echarts.utils';

import {SpiUtils} from './services/spi.utils';
import {SpiFilterPipe} from './pipes/spi-filter.pipe';
import {CanAcessDirective} from './guards/can-access.Directive';
import {AccordionGroupComponent} from './component/accordion/accordion-group.component';
import {AccordionComponent} from './component/accordion/accordion.component';
import {ClickOutsideDirective} from './directives/click-outside.directive';
import {AgGridModule} from 'ag-grid-angular';
import {DragDropDirective} from './directives/drag-drop.directive';
import {NgSelectModule} from '@ng-select/ng-select';
import {Ng2SearchPipeModule} from 'ng2-search-filter';
import {NgxPaginationModule} from 'ngx-pagination';
import {ArchwizardModule} from 'angular-archwizard';
import {OrderModule} from 'ngx-order-pipe';
import {ToastrModule, ToastrService} from 'ngx-toastr';
import {PERFECT_SCROLLBAR_CONFIG, PerfectScrollbarConfigInterface, PerfectScrollbarModule} from 'ngx-perfect-scrollbar';
import {ConfirmDeleteComponent} from './component/confirm-delete/confirm-delete.component';
import {ImgFallbackDirective} from './directives/img-fallback.directive';
import {EquipesService} from './services/equipes.service';
import {User2LettrePipe} from './pipes/user2Lettre.pipe';
import {SearchUsersComponent} from './component/search-users/search-users.component';
import {UserImageComponent} from "./component/user-image/user-image.component";
import {FlotDirective} from "./directives/flot/flot.directive";
import {EasypiechartDirective} from "./directives/easypiechart/easypiechart.directive";
import {CommercialFileService} from "./services/commercialFiles.service";
import {NotificationListComponent} from "./component/notification-list/notification-list.component";
import { ShowEquipeComponent } from './component/show-equipe/show-equipe.component';
import {ConfirmActionComponent} from "./component/confirm-action/confirm-action.component";
import {SpiPaginationComponent} from "./component/spi-pagination/spi-pagination.component";
import {QualificationService} from "./services/qualification.service";
import {PointageService} from "./services/pointage.service";
import {NbhoursPipe} from "./pipes/nbhours.pipe";
import {MaillingService} from "./services/mailling.service";
import {DashboardQueryService} from './services/dashboard-query.service';
import {DpDatePickerModule} from 'ng2-date-picker';
import {ListUserImageComponent} from './component/list-user-image/list-user-image.component';
import {DashboardComponent} from '../routes/backoffice/dashboard/dashboard.component';
import {PivotTableComponent} from './component/pivot-table/pivot-table.component';
import {ValidatorUtils} from './utils/validator.utils';
import {SpiEchartsUtils} from './services/spi.echarts.utils';
import {SpiChartsService} from './services/spi-charts.service';
import {CommercialFileStatusPipe} from './pipes/CommercialFileStatusPipe';
import {CommercialFileEnergyService} from './services/commercialFilesEnergy.service';
import {QualificationEnergyService} from './services/qualificationEnergy.service';
import { SelectStatusComponent } from './component/select-status/select-status.component';
import {DateInputConverter} from './converter/DateInputConverter';
import {MomentDatePipe} from './pipes/moment-date.pipe';
import {TextDiffComponent} from "./component/text-diff/text-diff.component";

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
    wheelPropagation: true
};

// @dynamic
@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        AccordionModule.forRoot(),
        AlertModule.forRoot(),
        ButtonsModule.forRoot(),
        CarouselModule.forRoot(),
        CollapseModule.forRoot(),
        DatepickerModule.forRoot(),
        BsDatepickerModule.forRoot(),
        BsDropdownModule.forRoot(),
        ModalModule.forRoot(),
        PaginationModule.forRoot(),
        ProgressbarModule.forRoot(),
        RatingModule.forRoot(),
        TabsModule.forRoot(),
        TimepickerModule.forRoot(),
        TooltipModule.forRoot(),
        PopoverModule.forRoot(),
        TypeaheadModule.forRoot(),

        // Addes by SPIDASH TEAM
        ArchwizardModule,
        Ng2SearchPipeModule, //including into imports
        OrderModule, // importing the sorting package here
        NgxPaginationModule,
        AgGridModule.withComponents([]),
        NgSelectModule,
        PerfectScrollbarModule
    ],
    providers: [
        ColorsService,
        ToastrService,
        UserService,
        EquipesService,
        QualificationService,
        QualificationEnergyService,
        PointageService,
        CommercialFileService,
        AuthenticationService,
        MaillingService,
        AuthGuard,
        EchartsUtils,
        AuthGuard,
        CanAcessDirective, DashboardQueryService,
        SpiUtils, ValidatorUtils, SpiEchartsUtils, SpiChartsService, CommercialFileEnergyService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AdminHttpInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true,
        },
        {
            provide: PERFECT_SCROLLBAR_CONFIG,
            useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG
        }
    ],
    declarations: [
        SparklineDirective,
        CheckallDirective,
        NowDirective,
        JqcloudDirective,
        AccordionGroupComponent,
        AccordionComponent,
        SpiFilterPipe, User2LettrePipe, NbhoursPipe, CommercialFileStatusPipe, MomentDatePipe,
        CanAcessDirective,
        ImgFallbackDirective,
        ClickOutsideDirective,
        DragDropDirective,
        ConfirmDeleteComponent,
        UserImageComponent,
        SearchUsersComponent,
        SelectStatusComponent,
        FlotDirective,
        EasypiechartDirective,
        NotificationListComponent,
        ShowEquipeComponent,
        ConfirmActionComponent,
        SpiPaginationComponent,
        ListUserImageComponent,
        DashboardComponent,
        PivotTableComponent,
        DateInputConverter, TextDiffComponent
  ],
    exports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        RouterModule,
        AccordionModule,
        AlertModule,
        ButtonsModule,
        CarouselModule,
        CollapseModule,
        DatepickerModule,
        BsDatepickerModule,
        BsDropdownModule,
        ModalModule,
        PaginationModule,
        ProgressbarModule,
        RatingModule,
        TabsModule,
        TimepickerModule,
        TooltipModule,
        PopoverModule,
        TypeaheadModule,
        ToastrModule,
        SparklineDirective,
        CheckallDirective,
        NowDirective,
        JqcloudDirective,
        ImgFallbackDirective,
        OrderModule,
        // -- Modules
        AgGridModule,
        NgSelectModule,
        Ng2SearchPipeModule, //including into imports
        NgxPaginationModule,
        // -- pipes
        SpiFilterPipe, User2LettrePipe, NbhoursPipe, CommercialFileStatusPipe, MomentDatePipe,
        // -- Directive
        CanAcessDirective,
        ClickOutsideDirective,
        DragDropDirective,
        // -- Component
        AccordionGroupComponent,
        AccordionComponent,
        UserImageComponent,
        ConfirmDeleteComponent,
        SearchUsersComponent,
        SelectStatusComponent,
        FlotDirective,
        EasypiechartDirective,
        NotificationListComponent, ShowEquipeComponent, ConfirmActionComponent, SpiPaginationComponent,
        ListUserImageComponent,
        DashboardComponent,
        PivotTableComponent,
        DateInputConverter, TextDiffComponent
    ]
})

// https://github.com/ocombe/ng2-translate/issues/209
export class SpiSharedModule {
    static forRoot(): ModuleWithProviders {
        return {
            ngModule: SpiSharedModule
        };
    }
}
